<template>
  <div class="py-1 bg-pitch-black">
    <!-- Fixed Header and Actions -->
    <div
      class="bg-pitch-black flex justify-between items-center pt-[14px] pb-[12px] px-5 mt-[-5px] border-b-2 border-rose-light fixed w-full z-10"
    >
      <!-- Title or Search Bar -->
      <div class="flex-1 flex items-center justify-start">
        <!-- Add Icon -->
        <button
          v-if="!showSearch"
          @click="goToAddContact"
          class="text-prime-golden text-2xl"
        >
          <i class="fa fa-plus"></i>
        </button>
        <p v-if="!showSearch" class="text-gray-400 text-xs flex-1 text-left ml-4">
          Showing all contacts ({{ contacts.length }})
        </p>
        <div v-else class="mt-1 w-full min-w-64 p-0">
          <input
            type="text"
            v-model="searchQuery"
            @input="searchContacts"
            class="w-full bg-pitch-black outline-none rounded-md text-md text-total-white"
            placeholder="Search contacts..."
          />
        </div>
      </div>

      <!-- Icons Section -->
      <div class="flex items-center space-x-6">
        <!-- Search Icon -->
        <button
          v-if="!showSearch"
          @click="toggleSearch"
          class="text-prime-golden text-lg"
        >
          <i class="fa fa-search"></i>
        </button>

        <!-- Close Search Icon -->
        <button v-else @click="toggleSearch" class="text-prime-golden">
          <div class="bg-gray-500 rounded-full w-4 h-4 flex items-center justify-center">
            <i class="fas fa-times text-xs text-black"></i>
          </div>
        </button>
      </div>
    </div>

    <!-- Scrollable Contact List -->
    <div class="overflow-y-auto cards-section-height mt-16 pb-[200px]">
      <div v-if="filteredContacts.length">
        <ContactCard
          v-for="contact in filteredContacts"
          :key="contact.id"
          :contactId="contact.id"
          :avatar="contact.profile_image_url"
          :name="getContactName(contact)"
          :role="contact.role"
          :phoneNumber="contact.phone"
          @optionsClicked="openDrawer"
          @viewContact="viewContact"
        />
      </div>

      <!-- No Contacts Found Message -->
      <div v-else>
        <p
          v-if="searchQuery"
          class="text-gray-400 cards-section-height flex justify-center items-center"
        >
          No contacts match your search.
        </p>
        <p
          v-else
          class="text-gray-400 cards-section-height flex justify-center items-center"
        >
          <button
            @click="goToCreateContact"
            class="text-prime-golden flex items-center gap-3"
          >
            <img src="../assets/images/add-icon.svg" class="w-6 h-6" />
            Click to add a contact!
          </button>
        </p>
      </div>
    </div>

    <!-- Contact Drawer -->
    <ContactDrawer
      :isOpen="isDrawerOpen"
      :contact="selectedContact"
      :projectId="projectId"
      :currentTab="'contacts'"
      @close="closeDrawer"
      @refresh="fetchContacts"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount } from "vue";
import { useContactsStore } from "@/stores/contact";
import { useRoute, useRouter } from "vue-router";
import ContactCard from "@/components/ContactCard.vue";
import ContactDrawer from "@/components/ContactDrawer.vue";

const isLoading = ref(false);
const error = ref(null);
const searchQuery = ref("");
const showSearch = ref(false);

const contacts = computed(() => contactsStore.contacts);

const route = useRoute();
const router = useRouter();
const projectId = computed(() => route.params.id); // Assuming route.params.id is the project ID
const contactsStore = useContactsStore();

const fetchContacts = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    await contactsStore.fetchContactsByProjectId(projectId.value);
    contacts.value = contactsStore.contacts;
  } catch (err) {
    error.value = err.message || "Failed to load contacts.";
  } finally {
    isLoading.value = false;
  }
};
// Search contacts
const searchContacts = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    await contactsStore.searchContactsByProjectId(projectId.value, searchQuery.value);
  } catch (err) {
    error.value = err.message || "Failed to search contacts.";
  } finally {
    isLoading.value = false;
  }
};

// Computed property for filtered contacts
const filteredContacts = computed(() => {
  let filtered = [];
  if (!searchQuery.value) {
    filtered = contacts.value;
  } else {
    filtered = contacts.value.filter((contact) =>
      getContactName(contact).toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Check for Project Manager and place at index 0
  const projectManager = filtered.find((contact) => contact.role === "Project Manager");
  if (projectManager) {
    filtered = filtered.filter((contact) => contact.id !== projectManager.id);
    filtered.unshift(projectManager);
  }

  return filtered;
});

const toggleSearch = () => {
  showSearch.value = !showSearch.value;
  if (!showSearch.value) {
    searchQuery.value = "";
  }
};

const isDrawerOpen = ref(false);
const selectedContact = ref(null);

const openDrawer = (contactId) => {
  const contact = contacts.value.find((c) => c.id === contactId);
  if (contact) {
    selectedContact.value = contact;
    isDrawerOpen.value = true;
  } else {
    console.error("Contact not found");
  }
};

watch(searchQuery, async (newQuery) => {
  if (newQuery) {
    await searchContacts();
  } else {
    await fetchContacts();
  }
});
const closeDrawer = () => {
  isDrawerOpen.value = false;
};

const viewContact = (contactId) => {
  router.push({
    name: "ContactViewPage",
    params: { id: contactId },
    query: { projectId: projectId.value },
  });
};

const getContactName = (contact) => {
  let baseName = `${contact.first_name} ${contact.last_name}`;

  if (baseName == " " || baseName == null || baseName == "Unknown Unknown") {
    baseName = contact.phone;
  }

  if (baseName == null) {
    baseName = contact.email;
  }

  return baseName;
};

const goToCreateContact = () => {
  console.log("projectId.value", projectId.value);
  router.push({ name: "CreateContactPage", params: { projectId: projectId.value } });
};

const goToAddContact = () => {
  router.push({ name: "AddContactToProject", params: { projectId: projectId.value } });
};

onMounted(() => {
  fetchContacts();
});

onBeforeUnmount(() => {
  contactsStore.clearData();
});
</script>

<style scoped>
.cards-section-height {
  height: calc(100vh - 10rem);
  overflow-y: auto;
}
</style>
